import type { Multirange, Range } from '@attain-apps/range'
import type { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as expression from '../expression'
import type { Mutation } from '../mutation'
import { Record } from '../record'

import { Subquery } from './subquery'
import { Target } from './target'
import { Variable } from './variable'

// TODO: maybe add a concept of version so if the type changes over time, old saved queries still work?
// TODO: better document
export interface Query<out T = any> {
  /**
   * Results are based on records of this entity. If a dimension id is given,
   * then all entities in that dimension are used, unless this is a subquery and
   * the dimension ID is for the dimension containing the parent query's entity,
   * in which case only the ancestors of the parent query's entity are used
   *
   * Target can also accept an array of date ranges. For each provided date range,
   * the query will generate a separate result set, where each result contains:
   * - The corresponding date range as 'period'
   * - Any subquery results filtered to that period
   *
   * @default no query entity, meaning only one aggregate item is returned
   */
  readonly target?: Target
  /**
   * Determines what is returned for each query entity (or for the aggregate item
   * if `entity` is not defined)
   *
   * @default determined for the query
   */
  readonly selection?: expression.Expression<T>
  /**
   * TODO:
   *
   * @default no subqueries
   */
  readonly subqueries?: Readonly<Subquery.Lookup>
  /**
   * Values to use for variables referenced in the query
   */
  readonly variables?: Readonly<Variable.Lookup>
  /**
   * Query is ran as if this/these mutation(s) have been applied
   *
   * This can be used to see the results of a given mutation without actually
   * applying that mutation to the zone
   */
  readonly assumption?: Maybe.ReadonlyArray<Mutation>
  /**
   * Used to sort the results
   *
   * @default sorts ascending by id
   */
  readonly sort?: {
    /**
     * @default { '@.': '!id' }
     */
    readonly by?: expression.fn.Fn<number>
    /**
     * @default false
     */
    readonly descending?: boolean
  }
  /**
   * Filters by ID and sets {@link quantity} to `1`
   */
  readonly id?: Record.Id
  /**
   * Filters the results. Must evaluate to a boolean
   *
   * @default no filter
   */
  readonly filter?: expression.fn.Fn<boolean>
  /**
   * Only returns records that are active in this period
   *
   * __NOTE:__ a record is active in the given period if any part of the
   * record's active period or date occurs in any part of the the given period;
   * i.e., if the two have any overlap
   *
   * @default does not filter on period
   */
  readonly period?: expression.Expression<Multirange<Date> | Range<Date>>
  /**
   * Filters and sorts records based on the given configuration
   *
   * @example
   * // Returns records with a purpose that has partial ID `?abc` or no purpose (sorted in that order)
   * {
   *   ...,
   *   purpose: { include: '?abc' }
   * }
   *
   * // Returns records with a purpose that has partial ID `?abc`, purpose that has partial ID '?def', or no purpose (sorted in that order)
   * {
   *   ...,
   *   purpose: { include: ['?abc', '?def'] }
   * }
   *
   * // Returns only records with purpose that has partial ID `?abc`
   * {
   *   ...,
   *   purpose: { include: '?abc', nil: 'exclude' }
   * }
   *
   * // Returns records with no purpose, purpose that has partial ID `?abc`, or purpose that has partial ID '?def' (sorted in that order)
   * {
   *   ...,
   *   purpose: { include: ['?abc', '?def'], nil: 'first' }
   * }
   *
   * // Returns every record, regardless of purpose
   * {
   *   ...,
   *   purpose: { include: 'all' }
   * }
   *
   * // Returns every record that has at least one purpose
   * {
   *   ...,
   *   purpose: { include: 'all', nil: 'exclude' }
   * }
   */
  readonly purpose?: {
    /**
     * Only returns entities with this/these purpose(s). If multiple purpose(s)
     * are given, they are sorted in the order given (this sorting is secondary to
     * {@link sort})
     *
     * @default For subqueries, the default is to inherit from the parent query.
     * For queries, only records without purposes are returned
     */
    readonly include?: Maybe.ReadonlyArray<PartialId> | 'all'
    /**
     * Determines how records without any purposes are treated
     *
     * Records without purposes are considered generally useful and accurate,
     * but potentially less specialized to a specific use case than records with
     * purposes
     *
     * @default For subqueries, the default is to inherit from the parent query.
     * For queries: 'last'
     */
    readonly nil?: 'first' | 'last' | 'exclude'
  }
  readonly quantity?: number
  readonly skip?: number
  /**
   * There are a handful of cases (listed below) where we return a single value
   * rather than a list of values. Setting this to `true` disables that behavior
   * in all cases, causing the result to be a list no matter what.
   *
   * Cases that return single values:
   * 1. If `1` is explicitly passed to {@link quantity}
   * 1. If this is a {@link Subquery} that is {@link target targeting} an
   *    ancestor of the parent {@link Query}
   * 1. If this is a {@link Subquery} that is using a {@link Subquery.ref} where
   *    the reference attribute belongs to the {@link target} of the parent
   *    {@link Query} AND the {@link Subquery.ref} is _not_ inheriting from
   *    ancestors
   *
   * __NOTE:__ this only has an effect when a {@link target} is provided. When
   * there is no {@link target}, you get a single value no matter what
   *
   * @default false
   */
  readonly noUnpacking?: boolean
}
export namespace Query {
  export const isQuery = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Query>({
      assumption: TypeChecker.isAny, // TODO:
      filter: TypeChecker.Factory.optional(
        expression.fn.Fn.isFn as TypeChecker<expression.fn.Fn<boolean>>,
      ),
      id: TypeChecker.Factory.optional(Record.Id.isId),
      noUnpacking: TypeChecker.Factory.optional(TypeChecker.isBoolean),
      period: TypeChecker.isAny, // TODO:
      purpose: TypeChecker.isAny, // TODO:
      quantity: TypeChecker.Factory.optional(TypeChecker.isNumber),
      selection: TypeChecker.Factory.optional(
        expression.Expression.isExpression,
      ),
      skip: TypeChecker.Factory.optional(TypeChecker.isNumber),
      sort: TypeChecker.isAny, // TODO:
      subqueries: TypeChecker.Factory.optional(Subquery.Lookup.isLookup),
      target: TypeChecker.Factory.optional(Target.isTarget),
      variables: TypeChecker.Factory.optional(Variable.Lookup.isLookup),
    }),
  )
  export const assertIsQuery: TypeAsserter<Query> =
    TypeAsserter.Factory.from(isQuery)

  /**
   * Returns this query and all of its subqueries in a single list
   */
  export function allIn(query: Query): Query[] {
    return [query, ...Subquery.allIn(query)]
  }

  /**
   * Does not include nested expressions or expressions used in subqueries
   */
  export function getTopLevelExpressions(
    query: Query,
  ): expression.Expression[] {
    return [query.selection, query.filter].filter(TypeChecker.isNotNullish.raw)
  }
}
